import { useTrackSearchEntry } from '@components/listings-search-bar/adapters/hooks/use-track-search-entry';
import {
	isSemanticSuggestionsOpenAtom,
	isTopCategoriesOpenAtom,
	semanticSearchTypeaheadAtom,
} from '@components/listings-search-bar/atoms';
import { useIsImproveMpPerformanceTier2Enabled } from '@hooks/use-improve-mp-performance-tier-2';
import { useAppSelector } from '@redux/hooks';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { useSetAtom } from 'jotai';
import React, { type FunctionComponent } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import Pill from '../../../../../../components/shared/PillWithDropdown/Pill';
import Styles from './styles.scss';

const SearchPillLabel = () => (
	<div className={Styles.innerAlign}>
		{/*
		 * Decorative: the adjacent "Search" text is the button's accessible name.
		 * Orion renders this as `<ods-icon-asset role="img">`, so giving the icon
		 * its own aria-label makes it contribute a second "Search" and the button
		 * announces "Search Search".
		 */}
		<Icon name="magnifier" size="sm" aria-hidden />
		<span>Search</span>
	</div>
);

/**
 * Mobile web: the "Search" affordance that sits beside "Filters" in the sticky
 * filter bar once the header's search box has scrolled out of view.
 *
 * Mobile collapses to a *button* rather than embedding a live search field the
 * way desktop does — per the `mWeb / Listings` design (Listings H2 2026, node
 * 661:33203), where the sticky bar is exactly `[Filters] [Search]`.
 *
 * Tapping it opens the mobile search modal the header already renders:
 * `MobileSemanticSuggestions` stays mounted for the life of the page (
 * `SemanticTopBar`'s mobile branch never passes `hideSearchBar`) and its
 * visibility is driven by these atoms. So this only has to flip the atoms — no
 * second search bar is mounted, which also keeps the static
 * `listings-search-bar-input` id unique.
 */
const PillSearch: FunctionComponent = () => {
	const setIsSemanticSuggestionsOpen = useSetAtom(
		isSemanticSuggestionsOpenAtom,
	);
	const setIsTopCategoriesOpen = useSetAtom(isTopCategoriesOpenAtom);
	const setSemanticSearchTypeahead = useSetAtom(semanticSearchTypeaheadAtom);
	const trackSearchEntry = useTrackSearchEntry();
	const categoryCode = useAppSelector((state) => state.category.code);
	const isImproveMpPerformanceTier2Enabled =
		useIsImproveMpPerformanceTier2Enabled();

	const handleClick = () => {
		// Open synchronously so the modal lands in the same paint as the tap —
		// deferring it leaves a blank frame between the bar and the modal. Same
		// reasoning as the mobile branch of `handleFocus` in
		// ListingsSearchBarConnected.
		// TODO: Remove unstable_batchedUpdates after React 18 upgrade — auto-batching makes this unnecessary
		unstable_batchedUpdates(() => {
			// Clear stale typeahead results first: the modal renders suggestions
			// whenever `suggestions.length > 0` and only falls back to top
			// categories when it's empty, so without this the modal can open onto
			// results from an earlier query. Mirrors the `resetSuggestions()` call
			// on the control path.
			setSemanticSearchTypeahead({
				storefronts: [],
				semanticSearchResults: [],
			});
			setIsSemanticSuggestionsOpen(true);
			setIsTopCategoriesOpen(true);
		});

		// Deferred for INP so the modal paints before analytics runs — the
		// established pattern in ListingsSearchBarConnected.
		//
		// `click_in_search` is reused rather than adding a new event, so this
		// entry point stays comparable with control, where tapping the header
		// search box fires the same event. Control additionally fires
		// `open_search_menu` from the search bar's internal tap path, which this
		// pill bypasses — flagged on GS-1187 for an analytics decision rather
		// than duplicating that event's construction here.
		setTimeout(() => {
			trackSearchEntry('click_in_search', { categoryCode });
		}, 0);
	};

	return (
		<Pill
			label={<SearchPillLabel />}
			onClick={handleClick}
			selected={false}
			isImproveMpPerformanceTier2Enabled={isImproveMpPerformanceTier2Enabled}
		/>
	);
};

export default PillSearch;
